import React from 'react';
import { RotateCcw } from 'lucide-react';
import RdvDetailContent from '../rendezVous/RdvDetailContent';
import { getRdvModalTitle } from '../../utils/rdvStatusUx';
import { formatEuro, getRdvPaymentView, getRdvSoldeRemboursable, canRefundRendezVous } from '../../utils/rdvPaymentDisplay';

/**
 * Coque modal RDV validée Mes commandes — contenu via RdvDetailContent.
 */
export default function MesCommandesRdvDetailModal({
  commande,
  statusDisplay,
  subtitle = 'Détail du rendez-vous — Mes commandes',
  isUpdating = false,
  rdvActions = [],
  encaisserSoldeUi,
  onClose,
  onRdvAction,
  onOpenRefund,
  onEncaisserSolde,
  getRdvActionButtonClass,
  getRdvActionIcon,
  getRdvConfirmationMeta,
}) {
  if (!commande) return null;

  const soldeRemboursableRdv = getRdvSoldeRemboursable(commande);
  const canRefundRdv = canRefundRendezVous(commande);

  const actionsFooter = (
    <div className="border-t pt-4">
      <h4 className="mb-3 font-semibold text-gray-700">Actions</h4>
      {rdvActions.length > 0 ? (
        <div className="grid gap-3 md:grid-cols-2">
          {rdvActions.map((action) => {
            const buttonClass = getRdvActionButtonClass(action.status);
            const { icon } = getRdvActionIcon(action.status);
            return (
              <button
                key={action.status}
                type="button"
                onClick={() =>
                  onRdvAction(
                    commande,
                    action.status,
                    getRdvConfirmationMeta?.(commande, action.status),
                  )
                }
                disabled={isUpdating}
                className={`flex items-center justify-center rounded-lg px-4 py-3 text-sm font-medium transition ${buttonClass} disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {icon}
                <span className="ml-2">{action.label}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-gray-500">Aucune action disponible pour ce rendez-vous.</p>
      )}

      {canRefundRdv ? (
        <button
          type="button"
          onClick={() => onOpenRefund(commande)}
          disabled={isUpdating}
          className="mt-3 flex w-full items-center justify-center rounded-lg bg-orange-600 px-4 py-3 text-sm font-medium text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RotateCcw className="h-5 w-5 mr-2" />
          Rembourser (Stripe) — solde {formatEuro(soldeRemboursableRdv)}
        </button>
      ) : null}

      {(() => {
        const pay = getRdvPaymentView(commande);
        const due = Number(pay?.amountDueLater || 0);
        if (commande.statut !== 'TERMINE' || !(due > 0)) return null;
        return (
          <div className="mt-4 space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm text-amber-900">
              Solde restant : <strong>{formatEuro(due)}</strong>
            </p>
            <select
              value={encaisserSoldeUi?.methode ?? 'CARTE'}
              onChange={(e) => encaisserSoldeUi?.onMethodeChange?.(e.target.value)}
              disabled={encaisserSoldeUi?.loading || !encaisserSoldeUi?.hasSession}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              {['ESPECES', 'CARTE', 'CHEQUE', 'VIREMENT'].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            {!encaisserSoldeUi?.hasSession && !encaisserSoldeUi?.probing ? (
              <p className="text-xs text-amber-800">Ouvrez la caisse pour encaisser le solde.</p>
            ) : null}
            {encaisserSoldeUi?.error ? (
              <p className="text-xs text-red-600">{encaisserSoldeUi.error}</p>
            ) : null}
            <button
              type="button"
              disabled={
                encaisserSoldeUi?.loading ||
                !encaisserSoldeUi?.hasSession ||
                encaisserSoldeUi?.probing
              }
              onClick={onEncaisserSolde}
              className="w-full rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {encaisserSoldeUi?.loading ? '…' : `Encaisser le solde ${formatEuro(due)}`}
            </button>
          </div>
        );
      })()}

      <button
        type="button"
        onClick={onClose}
        className="mt-4 w-full rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50"
      >
        Fermer
      </button>
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rdv-detail-modal-title"
      >
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div className="min-w-0 pr-4">
              <h3 id="rdv-detail-modal-title" className="text-2xl font-bold text-gray-900">
                {getRdvModalTitle(commande)}
              </h3>
              <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 shrink-0"
              aria-label="Fermer"
            >
              <span className="text-3xl leading-none">&times;</span>
            </button>
          </div>
          <RdvDetailContent
            commande={commande}
            statusDisplay={statusDisplay}
            footer={actionsFooter}
            showCommercant
          />
        </div>
      </div>
    </div>
  );
}
